import type { ReactNode } from "react";
import type { V2Bag, V2Value } from "@/features/applications/api/applications.api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { FieldInput } from "../FieldInput";
import { SectionCard } from "../primitives";
import { useReadOnly } from "../readonly";
import { PRICING_FIELDS } from "../steps";
import { formatDateTime } from "@/utils/dates";
import { RateExceptionCard, type RateExceptionActions } from "./RateExceptionCard";
import { onlineOrderingNeedsSecondApp } from "../pricing";

const FIELD_BY_KEY = new Map(PRICING_FIELDS.map((d) => [d.key, d]));

export function PricingConfigurator({
  bag, onChange, onSubmit, onSave, submitting = false, saving = false, exceptionActions,
}: {
  bag: V2Bag;
  onChange: (key: string, value: V2Value) => void;
  onSubmit?: () => void;
  onSave?: () => void;
  submitting?: boolean;
  saving?: boolean;
  exceptionActions?: RateExceptionActions;
}) {
  const readOnly = useReadOnly();

  const field = (key: string) => {
    const def = FIELD_BY_KEY.get(key);
    if (!def || (def.showWhen && !def.showWhen(bag))) return null;
    return <FieldInput def={def} value={bag[key] ?? null} bag={bag} onChange={(v) => onChange(key, v)} />;
  };

  const inline = (key: string, label: string) => {
    const def = FIELD_BY_KEY.get(key);
    if (!def || (def.showWhen && !def.showWhen(bag))) return null;
    return <FieldInput key={key} def={def} value={bag[key] ?? null} bag={bag} inlineLabel={label} onChange={(v) => onChange(key, v)} />;
  };

  const addOn = (key: string, label: string, detail: ReactNode) => {
    const on = bag[key] === true;
    return (
      <div className="flex min-h-11 flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-100 py-2">
        <Label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium text-slate-800">
          <Switch className="h-4 w-7 [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-3" checked={on} disabled={readOnly} onCheckedChange={(c) => onChange(key, c)} />
          {label}
        </Label>
        {on && <div className="flex flex-wrap items-center gap-2">{detail}</div>}
      </div>
    );
  };

  const enteredBy = typeof bag.pricingEnteredBy === "string" ? bag.pricingEnteredBy : null;
  const enteredRole = typeof bag.pricingEnteredByRole === "string" ? bag.pricingEnteredByRole : null;
  const enteredAt = typeof bag.pricingEnteredAt === "string" ? bag.pricingEnteredAt : null;

  return (
    <SectionCard title="Configure pricing" contentClassName="p-0">
      <div className="space-y-4 p-5">
        {enteredBy && (
          <p className="-mt-1 text-xs text-slate-500">
            Pricing entered by <span className="font-medium text-slate-700">{enteredBy}</span>
            {enteredRole && ` (${enteredRole})`}
            {enteredAt && ` · ${formatDateTime(enteredAt)}`}
          </p>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[field("pricingProgram"), field("pricingRatePct"), field("pricingPerTxnFee")].map((c, i) => (c ? <div key={i}>{c}</div> : null))}
        </div>
        <RateExceptionCard bag={bag} actions={exceptionActions} />
        <div className="border-b border-slate-100">
          {addOn("amexCustomRate", "Separate Amex rate", inline("amexRatePct", "Rate %"))}
          {addOn("pinDebitEnabled", "PIN debit", <>{inline("pinDebitRatePct", "Rate %")}{inline("pinDebitPerItemFee", "Per item $")}</>)}
          {addOn("onlineOrdering", "Online ordering", onlineOrderingNeedsSecondApp(bag.pricingProgram, bag.pricingRatePct) ? (
              <span className="text-xs text-slate-500">Separate MPA · Flat Rate 3.3% + $0.30</span>
            ) : (
              <span className="text-xs text-slate-500">Provided by BLogic</span>
            ))}
            {addOn("sensaInvoices", "SensaInvoices", null)}
          {addOn("acceptAch", "Accept ACH", <>{inline("achRatePct", "Rate %")}{inline("achFeePerTxn", "Per transaction $")}</>)}
        </div>
        {field("pricingNotesForBackOffice")}
      </div>
      {onSave && !onSubmit && !readOnly && (
        <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-3">
          <Button type="button" onClick={onSave} disabled={saving || submitting}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      )}
      {onSubmit && !readOnly && (
        <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-3">
          <Button type="button" onClick={onSubmit} disabled={saving || submitting}>
            {submitting ? "Submitting…" : "Submit"}
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

